import React, { useState } from 'react';
import { ExclamationCircleIcon, ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

export const FLAG_STYLE = {
  alert: { icon: ExclamationCircleIcon, box: 'border-red-200 bg-red-50 text-red-800' },
  caution: { icon: ExclamationTriangleIcon, box: 'border-amber-200 bg-amber-50 text-amber-900' },
  note: { icon: InformationCircleIcon, box: 'border-slate-200 bg-slate-50 text-slate-700' },
};
export const RELATIONSHIP = { self: 'the patient', parent: 'a parent', guardian: 'a guardian', other: 'someone helping them' };

/**
 * PreopAnswers — a patient's pre-op questionnaire as the anaesthetist reads it:
 * what the screening rules flagged first, then the answers section by section
 * (by default only the ones worth a look — "yes", "not sure", lists and notes).
 *   q: the questionnaire from GET /api/preop/:id (flags, summary, bmi, stop_bang)
 */
const PreopAnswers = ({ q }) => {
  const [all, setAll] = useState(false);
  const flags = q.flags || [];
  const sb = q.stop_bang;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 text-xs">
        <span className="rounded-full bg-slate-100 px-2.5 py-1 font-semibold text-slate-700">BMI {q.bmi ?? 'unknown'}</span>
        {sb && <span className="rounded-full bg-slate-100 px-2.5 py-1 font-semibold text-slate-700">STOP-BANG {sb.score}/8{sb.known < 8 ? ` (${sb.known} of 8 known)` : ''}</span>}
        {q.completed_by_relationship && q.completed_by_relationship !== 'self' && (
          <span className="rounded-full bg-blue-50 px-2.5 py-1 font-semibold text-blue-800">Filled in by {q.completed_by_name}, {RELATIONSHIP[q.completed_by_relationship]}</span>
        )}
      </div>

      {flags.length ? (
        <ul className="space-y-1.5">
          {flags.map((f) => {
            const s = FLAG_STYLE[f.level] || FLAG_STYLE.note;
            const Icon = s.icon;
            return (
              <li key={`${f.code}-${f.message}`} className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${s.box}`}>
                <Icon className="mt-0.5 h-4 w-4 shrink-0" /> <span>{f.message}</span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">Nothing flagged.</p>
      )}
      <p className="text-[11px] text-slate-400">Flags come from fixed screening rules — prompts for the anaesthetist, not a diagnosis or an ASA grade.</p>

      <div className="rounded-lg border border-slate-200">
        <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
          <h3 className="text-sm font-bold text-slate-700">Answers</h3>
          <button onClick={() => setAll(!all)} className="text-xs font-semibold text-brand-teal hover:underline">{all ? 'Only what needs a look' : 'Show every answer'}</button>
        </div>
        <div className="divide-y divide-slate-100">
          {(q.summary || []).map((s) => {
            const items = s.items.filter((i) => all || i.positive);
            if (!items.length) return null;
            return (
              <div key={s.id} className="px-3 py-2">
                <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-400">{s.title}</div>
                <dl className="space-y-1 text-sm">
                  {items.map((i) => (
                    <div key={i.id} className="grid grid-cols-5 gap-2">
                      <dt className="col-span-2 text-slate-500">{i.label}</dt>
                      <dd className={`col-span-3 ${i.positive ? 'font-semibold text-slate-800' : 'text-slate-600'}`}>
                        {i.value}
                        {i.detail && <span className="block font-normal text-slate-600">{i.detail}</span>}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            );
          })}
          {!all && !(q.summary || []).some((s) => s.items.some((i) => i.positive)) && (
            <p className="px-3 py-3 text-sm text-slate-500">Every answer was “no”.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PreopAnswers;
